import React from 'react';
import { LucideIcon } from 'lucide-react';
import { useCountUp } from '../../hooks/useCountUp';

interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease';
    period: string;
  };
  icon?: LucideIcon;
  iconColor?: string;
  iconBgColor?: string;
  trend?: 'up' | 'down' | 'neutral';
  className?: string;
  onClick?: () => void;
  showIcon?: boolean;
  animate?: boolean;
  animationDelay?: number;
}

const StatsCard: React.FC<StatsCardProps> = ({
  title,
  value,
  change,
  icon: Icon,
  iconColor = 'text-blue-600',
  iconBgColor = 'bg-blue-100',
  trend = 'neutral',
  className = '',
  onClick,
  showIcon = true,
  animate = false,
  animationDelay = 0
}) => {
  // Extrair valor numérico para animação
  const getNumericValue = (val: string | number): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      // Remove R$, pontos, vírgulas e espaços para extrair o número
      const cleanValue = val.replace(/[R$\s.]/g, '').replace(',', '.');
      const parsed = parseFloat(cleanValue);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  };

  const numericValue = getNumericValue(value);
  const { displayValue, isCounting } = useCountUp({
    end: numericValue,
    duration: 1500,
    steps: 60,
    delay: animationDelay
  });

  // Formatar valor para exibição
  const formatDisplayValue = (val: number): string => {
    if (typeof value === 'string' && value.includes('R$')) {
      // É um valor monetário
      return `R$ ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    // É um número simples
    return Math.round(val).toLocaleString('pt-BR');
  };

  const getTrendColor = () => {
    switch (trend) {
      case 'up':
        return 'text-green-600';
      case 'down':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTrendIcon = () => {
    switch (trend) {
      case 'up':
        return '↗';
      case 'down':
        return '↘';
      default:
        return '→';
    }
  };

  return (
    <div 
      className={`group relative bg-gradient-to-br from-white to-slate-50/50 rounded-2xl border border-slate-200/60 shadow-lg shadow-slate-200/30 transition-all duration-500 hover:shadow-xl hover:shadow-slate-300/40 hover:-translate-y-2 hover:scale-[1.02] ${
        onClick ? 'cursor-pointer hover:border-slate-300' : ''
      } ${className} overflow-hidden`}
      onClick={onClick}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-transparent via-slate-50/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
      
      {/* Top Accent Line */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-400 via-teal-500 to-teal-600"></div>
      
      <div className="relative p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-500 mb-3 uppercase tracking-wide">
              {title}
            </p>
            
            <div className="flex items-baseline gap-3">
              <p className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent truncate">
                {animate ? (
                  <span className="inline-block">
                    {formatDisplayValue(displayValue)}
                    {isCounting && (
                      <span className="ml-1 text-teal-500 animate-pulse">|</span>
                    )}
                  </span>
                ) : (
                  value
                )}
              </p>
              
              {change && (
                <span className={`text-sm font-bold px-2 py-1 rounded-full ${getTrendColor()}`}>
                  {change.type === 'increase' ? '+' : ''}{change.value}%
                </span>
              )}
            </div>
            
            {change && (
              <p className="text-xs text-slate-400 mt-2 font-medium">
                {change.period}
              </p>
            )}
          </div>
          
          {showIcon && Icon && (
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${iconBgColor} shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500 relative overflow-hidden`}>
              {/* Icon Background Glow */}
              <div className={`absolute inset-0 ${iconBgColor} opacity-20 blur-sm group-hover:blur-md transition-all duration-500`}></div>
              <Icon className={`w-7 h-7 ${iconColor} relative z-10`} />
            </div>
          )}
        </div>
        
        {/* Bottom Decorative Element */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent"></div>
      </div>
    </div>
  );
};

export default StatsCard;
